import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock,
  TrendingUp,
  Search,
  MapPin,
  Users,
  BarChart3,
  Target,
  Globe,
  FileText,
  Shield
} from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { 
  getSEOAuditItems, 
  getSEOAuditSummary,
  getLocalCitations,
  getLocalKeywords,
  getSEOCompetitors,
  generateLocalBusinessSchema,
  injectSchemaMarkup
} from '../../services/seoService';

export default function LocalSEOOptimizationCenter() {
  const [auditItems, setAuditItems] = useState([]);
  const [auditSummary, setAuditSummary] = useState([]);
  const [citations, setCitations] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadSEOData();
    
    // Inject LocalBusiness schema for Sacramento County
    const businessSchema = generateLocalBusinessSchema({
      name: '3D Sailors Bail Bonds',
      phone: '(916) 555-0100',
      street: '123 Main Street',
      city: 'Sacramento',
      zip: '95814',
      url: 'https://sailorsbail.com',
    });
    injectSchemaMarkup(businessSchema);
  }, []);

  const loadSEOData = async () => {
    try {
      setLoading(true);
      const [itemsData, summaryData, citationsData, keywordsData, competitorsData] = await Promise.all([
        getSEOAuditItems(),
        getSEOAuditSummary(),
        getLocalCitations(),
        getLocalKeywords('Sacramento County'),
        getSEOCompetitors(),
      ]);

      setAuditItems(itemsData);
      setAuditSummary(summaryData);
      setCitations(citationsData);
      setKeywords(keywordsData);
      setCompetitors(competitorsData);
    } catch (error) {
      console.error('Error loading SEO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      technical_seo: <Shield className="w-5 h-5" />,
      on_page_seo: <FileText className="w-5 h-5" />,
      local_seo: <MapPin className="w-5 h-5" />,
      content_quality: <FileText className="w-5 h-5" />,
      backlinks: <Globe className="w-5 h-5" />,
      schema_markup: <BarChart3 className="w-5 h-5" />,
      mobile_optimization: <Target className="w-5 h-5" />,
      analytics: <TrendingUp className="w-5 h-5" />,
    };
    return iconMap?.[category] || <FileText className="w-5 h-5" />;
  };

  const formatCategoryName = (category) => {
    return category?.split('_')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || '';
  };

  const filteredItems = selectedCategory === 'all' 
    ? auditItems 
    : auditItems?.filter(item => item?.category === selectedCategory);

  const overallScore = auditSummary?.length > 0
    ? Math.round(
        (auditSummary?.reduce((acc, cat) => acc + (cat?.passed_items || 0), 0) / 
         auditSummary?.reduce((acc, cat) => acc + (cat?.total_items || 0), 0)) * 100
      )
    : 0;

  return (
    <>
      <Helmet>
        <title>Local SEO Optimization Center - Sacramento County Bail Bonds | 3D Sailors</title>
        <meta 
          name="description" 
          content="Comprehensive SEO optimization dashboard for Sacramento County bail bond services. Track local search rankings, manage citations, monitor competitors, and optimize for Google Analytics 4." 
        />
        <meta name="keywords" content="bail bonds seo, sacramento seo, local search optimization, google my business, sacramento bail bonds" />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <Header />
        
        <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Search className="w-4 h-4" />
              Local SEO Command Center
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Sacramento County SEO Optimization
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maximize local search visibility for bail bond services across Sacramento, Placer, and El Dorado counties
            </p>
          </div>

          {/* Overall Score Card */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 mb-8 text-white shadow-xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-blue-100 mb-2">Overall SEO Health Score</p>
                <p className="text-6xl font-bold">{overallScore}%</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 max-w-2xl">
                {auditSummary?.slice(0, 4)?.map((category) => (
                  <div key={category?.category} className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {getCategoryIcon(category?.category)}
                      <p className="text-sm font-medium">{formatCategoryName(category?.category)}</p>
                    </div>
                    <p className="text-2xl font-bold">
                      {Math.round(((category?.passed_items || 0) / (category?.total_items || 1)) * 100)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Category Filter Tabs */}
          <div className="mb-8 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === 'all' ?'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Items ({auditItems?.length})
              </button>
              {auditSummary?.map((category) => (
                <button
                  key={category?.category}
                  onClick={() => setSelectedCategory(category?.category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    selectedCategory === category?.category
                      ? 'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {getCategoryIcon(category?.category)}
                  {formatCategoryName(category?.category)} ({category?.total_items})
                </button>
              ))}
            </div>
          </div>

          {/* SEO Audit Items Grid */}
          <div className="grid gap-6 mb-12">
            <h2 className="text-2xl font-bold text-gray-900">SEO Audit Checklist</h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading SEO data...</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredItems?.map((item) => (
                  <div
                    key={item?.id}
                    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {getStatusIcon(item?.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {item?.item_name}
                            </h3>
                            <p className="text-gray-600 text-sm">
                              {item?.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                              Priority {item?.priority}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-4">
                          <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                            {getCategoryIcon(item?.category)}
                            {formatCategoryName(item?.category)}
                          </span>
                          {item?.last_checked_at && (
                            <span className="text-sm text-gray-400">
                              Last checked: {new Date(item.last_checked_at)?.toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {item?.notes && (
                          <p className="mt-3 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                            💡 {item?.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Local Citations Dashboard */}
          <div className="bg-white rounded-2xl p-8 mb-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Local Citations & Directories</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {citations?.map((citation) => (
                <div key={citation?.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{citation?.directory_name}</h3>
                    {citation?.is_verified && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>{citation?.business_name}</p>
                    <p>{citation?.address}</p>
                    <p>{citation?.phone}</p>
                  </div>
                  {citation?.listing_url && (
                    <a
                      href={citation?.listing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      View Listing →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Local Keywords Tracker */}
          <div className="bg-white rounded-2xl p-8 mb-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Sacramento County Keyword Rankings</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Keyword</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Location</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Search Volume</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Competition</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Current Rank</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Target Rank</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords?.map((keyword) => (
                    <tr key={keyword?.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{keyword?.keyword}</td>
                      <td className="py-3 px-4 text-gray-600">{keyword?.target_location}</td>
                      <td className="py-3 px-4 text-center text-gray-900">{keyword?.search_volume?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          keyword?.competition_level === 'high' ?'bg-red-100 text-red-700'
                            : keyword?.competition_level === 'medium' ?'bg-yellow-100 text-yellow-700' :'bg-green-100 text-green-700'
                        }`}>
                          {keyword?.competition_level}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-900">#{keyword?.current_ranking}</td>
                      <td className="py-3 px-4 text-center text-blue-600 font-medium">#{keyword?.target_ranking}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitor Analysis */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Competitor Analysis</h2>
            </div>
            <div className="grid gap-6">
              {competitors?.map((competitor, index) => (
                <div key={competitor?.id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-gray-400">#{index + 1}</span>
                        <h3 className="text-xl font-bold text-gray-900">{competitor?.competitor_name}</h3>
                      </div>
                      <a
                        href={competitor?.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm"
                      >
                        {competitor?.website_url} →
                      </a>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 mb-1">Domain Authority</p>
                      <p className="text-3xl font-bold text-gray-900">{competitor?.domain_authority}</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-green-900 mb-2">Strengths</p>
                      <p className="text-sm text-green-700">{competitor?.strengths}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-red-900 mb-2">Weaknesses</p>
                      <p className="text-sm text-red-700">{competitor?.weaknesses}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}